import { createFileRoute, redirect } from '@tanstack/react-router';
import { api, throwIfNotOk } from '../../lib/api';

/**
 * SIL-302 (W4, resources/plans/financials-hifi-flow-spec.md slice 1) — bare
 * `/financials` resolves the org's setup state and redirects to
 * `/financials/setup` while <6 steps are complete, else straight to
 * `/financials/book-vs-fmv`.
 *
 * MUST live in `beforeLoad` (never a `useEffect`) — a `useEffect`-driven
 * redirect renders the wrong screen for one paint first (a visible flash),
 * which is exactly what `beforeLoad` avoids by resolving before the route
 * ever mounts.
 *
 * Deterministic fallback: any error (network, 403 for a buyer-only org that
 * can't read a seller-only endpoint, etc.) OR a slow response falls back to
 * `/financials/book-vs-fmv` — a CFO must never be stranded on a spinner or
 * bounced into a redirect loop. `/financials/setup` itself is a sibling
 * route (`setup.tsx`) and stays directly addressable regardless of this
 * logic — nothing here gates it.
 */
const SETUP_FETCH_TIMEOUT_MS = 4000;

async function resolveSetupComplete(): Promise<boolean> {
  const timeout = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error('financials setup lookup timed out')), SETUP_FETCH_TIMEOUT_MS);
  });
  const fetchSetup = async () => {
    const res = await api.api.client.financials.setup.$get();
    await throwIfNotOk(res);
    return res.json();
  };
  const data = await Promise.race([fetchSetup(), timeout]);
  return data.completedCount >= data.totalSteps;
}

export const Route = createFileRoute('/financials/')({
  beforeLoad: async () => {
    let complete = true; // deterministic fallback destination on any failure
    try {
      complete = await resolveSetupComplete();
    } catch {
      complete = true;
    }
    throw redirect({ to: complete ? '/financials/book-vs-fmv' : '/financials/setup' });
  },
});
